// React hook for consent management
import { useState, useEffect, useCallback } from "react";
import { cmpService, CMPStatus, ConsentPreferences } from "../lib/utils/cmp";
import { detectLocation, LocationInfo } from "../lib/utils/location";
import { isDev } from "@/lib/utils/env";

export interface UseConsentReturn {
  // CMP status
  isLoaded: boolean;
  isReady: boolean;
  hasConsent: boolean;
  preferences: ConsentPreferences | null;

  // Location info
  location: LocationInfo | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  showBanner: () => void;
  hideBanner: () => void;
  refreshConsent: () => void;
}

export function useConsent(): UseConsentReturn {
  const [status, setStatus] = useState<CMPStatus>(cmpService.getStatus());
  const [location, setLocation] = useState<LocationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize CMP and detect location
  useEffect(() => {
    if (isDev) return;
    let mounted = true;

    const initialize = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const locationInfo = await detectLocation();
        await cmpService.initialize(locationInfo);

        if (!mounted) return;
        setLocation(locationInfo);
        setStatus(cmpService.getStatus());
      } catch (err) {
        if (mounted) {
          console.error("Failed to initialize consent management:", err);
          setError(err instanceof Error ? err.message : "Failed to initialize consent management");
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, []);

  // Subscribe to CMP status changes
  useEffect(() => {
    if (isDev) return;
    const unsubscribe = cmpService.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    return unsubscribe;
  }, []);

  // Actions
  const showBanner = useCallback(() => {
    cmpService.showBanner();
  }, []);

  const hideBanner = useCallback(() => {
    cmpService.hideBanner();
  }, []);

  const refreshConsent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const locationInfo = await detectLocation();
      setLocation(locationInfo);

      await cmpService.initialize(locationInfo);
      setStatus(cmpService.getStatus());
    } catch (err) {
      console.error("Failed to refresh consent:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh consent");
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    // CMP status
    isLoaded: status.isLoaded,
    isReady: status.isReady,
    hasConsent: status.hasConsent,
    preferences: status.preferences,

    // Location info
    location,
    isLoading,
    error,

    // Actions
    showBanner,
    hideBanner,
    refreshConsent,
  };
}

// Convenience hooks for specific consent types
export function useAnalyticsConsent(): boolean {
  const { hasConsent, preferences } = useConsent();
  return hasConsent && preferences?.analytics === true;
}

export function useMarketingConsent(): boolean {
  const { hasConsent, preferences } = useConsent();
  return hasConsent && preferences?.marketing === true;
}

export function useNecessaryConsent(): boolean {
  const { hasConsent, preferences } = useConsent();
  return hasConsent && preferences?.necessary === true;
}

export function usePreferencesConsent(): boolean {
  const { hasConsent, preferences } = useConsent();
  return hasConsent && preferences?.preferences === true;
}
